import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, Carousel, DrilldownModal, ExportButtonGroup,
  KpiCard, Tabs, useExport, useToast,
} from '@kss/ui';
import { useHasRole } from '@kss/auth';
import { useExportGate } from '../../hooks/useExportGate';
import {
  Receipt, IndianRupee, AlertTriangle, CheckCircle2, Clock, XCircle,
  RefreshCw, Lock, Search, X as XIcon, Printer,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import type { Invoice } from '@kss/api';
import { invoiceService, enumValue } from '@kss/api';
import { useThemeChartColors, DrillTable, type DrillColumn } from '../shared/dashboardHelpers';

const inr = (n: number) => `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

type KpiId = 'total' | 'revenue' | 'paid' | 'outstanding' | 'overdue' | 'draft';
interface KpiDef { id: KpiId; title: string; subtitle?: string; icon: React.ReactNode; compute: (r: Invoice[]) => { value: string | number }; detail: (r: Invoice[]) => Invoice[]; }

const isStatus = (o: Invoice, label: string) => o.status != null && o.status === enumValue('InvoiceStatus', label);

const KPIS: KpiDef[] = [
  { id: 'total', title: 'Total invoices', icon: <Receipt className="h-5 w-5 text-primary" />,
    compute: (r) => ({ value: r.length }), detail: (r) => r },
  { id: 'revenue', title: 'Total billed', icon: <IndianRupee className="h-5 w-5 text-success" />,
    compute: (r) => ({ value: inr(r.reduce((s, i) => s + Number(i.totalAmount || 0), 0)) }),
    detail: (r) => [...r].sort((a, b) => Number(b.totalAmount || 0) - Number(a.totalAmount || 0)) },
  { id: 'paid', title: 'Paid', icon: <CheckCircle2 className="h-5 w-5 text-success" />,
    compute: (r) => ({ value: r.filter((i) => isStatus(i, 'Paid')).length }),
    detail: (r) => r.filter((i) => isStatus(i, 'Paid')) },
  { id: 'outstanding', title: 'Outstanding', subtitle: 'Unpaid balance', icon: <AlertTriangle className="h-5 w-5 text-warning" />,
    compute: (r) => ({ value: inr(r.filter((i) => !isStatus(i, 'Paid') && !isStatus(i, 'Cancelled')).reduce((s, i) => s + Number(i.balanceAmount ?? i.totalAmount ?? 0), 0)) }),
    detail: (r) => r.filter((i) => !isStatus(i, 'Paid') && !isStatus(i, 'Cancelled')) },
  { id: 'overdue', title: 'Overdue', icon: <Clock className="h-5 w-5 text-danger" />,
    compute: (r) => ({ value: r.filter((i) => isStatus(i, 'Overdue')).length }),
    detail: (r) => r.filter((i) => isStatus(i, 'Overdue')) },
  { id: 'draft', title: 'Drafts', icon: <XCircle className="h-5 w-5 text-content-muted" />,
    compute: (r) => ({ value: r.filter((i) => isStatus(i, 'Draft') || !i.status).length }),
    detail: (r) => r.filter((i) => isStatus(i, 'Draft') || !i.status) },
];

const DRILL_COLUMNS: DrillColumn<Invoice>[] = [
  { key: 'invoiceNumber', header: 'Invoice #' },
  { key: 'invoiceDate', header: 'Date', render: (v) => v ? new Date(String(v)).toLocaleDateString() : '—' },
  { key: 'dueDate', header: 'Due', render: (v) => v ? new Date(String(v)).toLocaleDateString() : '—' },
  { key: 'status', header: 'Status' },
  { key: 'totalAmount', header: 'Total (₹)', align: 'right', render: (v) => Number(v ?? 0).toLocaleString('en-IN') },
  { key: 'paidAmount', header: 'Paid (₹)', align: 'right', render: (v) => Number(v ?? 0).toLocaleString('en-IN') },
  { key: 'customerId', header: 'Customer' },
];

type DrillContext = { title: string; subtitle?: string; rows: Invoice[] };
type TabId = 'kpis' | 'charts';

export default function InvoiceAnalytics() {
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const canView = useHasRole(['admin', 'manager']);
  const exportGate = useExportGate(['admin', 'manager']);
  const canExport = exportGate.enabled;

  const [rows, setRows] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('kpis');
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [openKpi, setOpenKpi] = useState<KpiDef | null>(null);
  const [chartDrill, setChartDrill] = useState<DrillContext | null>(null);
  const { exportToExcel, exportToPdf, printWidget } = useExport();
  const drillBodyRef = useRef<HTMLDivElement>(null);
  const chartDrillBodyRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const colors = useThemeChartColors();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await invoiceService.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load invoices'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (canView) load(); }, [canView, load]);
  useEffect(() => { if (searchOpen) searchInputRef.current?.focus(); }, [searchOpen]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((i) =>
      (i.invoiceNumber ?? '').toLowerCase().includes(q) ||
      String(i.status ?? '').toLowerCase().includes(q) ||
      (i.customerId ?? '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const kpis = useMemo(() => KPIS.map((def) => ({ def, ...def.compute(filteredRows) })), [filteredRows]);
  const kpiSlides = useMemo(() => {
    const chunks: (typeof kpis)[] = [];
    for (let i = 0; i < kpis.length; i += 4) chunks.push(kpis.slice(i, i + 4));
    return chunks.map((chunk, idx) => ({ id: idx,
      content: (
        <div className="grid h-full grid-cols-1 gap-3 p-1 sm:grid-cols-2 lg:grid-cols-4">
          {chunk.map(({ def, value }) => (<KpiCard key={def.id} title={def.title} subtitle={def.subtitle} value={value} icon={def.icon} onClick={() => setOpenKpi(def)} />))}
        </div>),
    }));
  }, [kpis]);

  const statusData = useMemo(() => {
    const byStatus = new Map<string, Invoice[]>();
    for (const i of filteredRows) {
      const k = String(i.status ?? 'Unknown');
      const l = byStatus.get(k) ?? []; l.push(i); byStatus.set(k, l);
    }
    return [...byStatus.entries()].map(([name, detail]) => ({ name, value: detail.length, detail }));
  }, [filteredRows]);

  const customerBarData = useMemo(() => {
    const map = new Map<string, { rows: Invoice[]; total: number }>();
    for (const i of filteredRows) {
      const k = i.customerId || 'Unassigned';
      const cur = map.get(k) ?? { rows: [], total: 0 };
      cur.rows.push(i); cur.total += Number(i.totalAmount ?? 0);
      map.set(k, cur);
    }
    return [...map.entries()].map(([k, v]) => ({ name: k.length > 14 ? `${k.slice(0, 6)}…${k.slice(-4)}` : k, fullKey: k, total: Math.round(v.total), detail: v.rows })).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [filteredRows]);

  const rowsToExcel = (list: Invoice[]) => list.map((i) => ({
    invoiceNumber: i.invoiceNumber ?? '', invoiceDate: i.invoiceDate ?? '', dueDate: i.dueDate ?? '',
    status: i.status ?? '', totalAmount: i.totalAmount, paidAmount: i.paidAmount ?? 0, customerId: i.customerId ?? '',
  }));
  const excelCols = [
    { key: 'invoiceNumber', header: 'Invoice #' }, { key: 'invoiceDate', header: 'Date' }, { key: 'dueDate', header: 'Due' },
    { key: 'status', header: 'Status' }, { key: 'totalAmount', header: 'Total' }, { key: 'paidAmount', header: 'Paid' }, { key: 'customerId', header: 'Customer' },
  ];
  const kpiDrillRows = openKpi ? openKpi.detail(filteredRows) : [];
  const kpiExcel = () => openKpi && exportToExcel(rowsToExcel(kpiDrillRows), excelCols, `invoices-${openKpi.id}`);
  const kpiPdf = () => openKpi && drillBodyRef.current && exportToPdf(drillBodyRef.current, `invoices-${openKpi.id}`);
  const chartExcel = () => chartDrill && exportToExcel(rowsToExcel(chartDrill.rows), excelCols, `invoices-${chartDrill.title.replace(/\s+/g, '-').toLowerCase()}`);
  const chartPdf = () => chartDrill && chartDrillBodyRef.current && exportToPdf(chartDrillBodyRef.current, chartDrill.title.replace(/\s+/g, '-').toLowerCase());
  const printDashboard = async () => { if (!dashboardRef.current) return; setPrinting(true); try { await exportToPdf(dashboardRef.current, 'invoices-dashboard'); } finally { setPrinting(false); } };

  if (!canView) return (
    <div className="-m-6 p-6">
      <div className="mx-auto mt-12 max-w-md rounded-xl border border-border bg-surface-card p-8 text-center">
        <Lock className="mx-auto h-8 w-8 text-content-muted" />
        <h2 className="mt-3 text-lg font-semibold text-content-primary">Access denied</h2>
        <p className="mt-1 text-sm text-content-muted">Your role does not permit viewing the Invoices dashboard.</p>
      </div>
    </div>
  );

  const kpisContent = <div className="space-y-3">{kpiSlides.length > 0 && <Carousel slides={kpiSlides} autoPlay={false} showArrows showDots height={200} />}</div>;
  const chartsContent = (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold text-content-primary">By status</h3><span className="text-[11px] text-content-muted">Click a slice</span></div>
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}
              onClick={(e: { payload?: { name: string; detail: Invoice[] } }) => e?.payload && setChartDrill({ title: `Invoices — ${e.payload.name}`, subtitle: `${e.payload.detail.length} row(s)`, rows: e.payload.detail })}
              className="cursor-pointer">
              {statusData.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <div className="mb-2 flex items-center justify-between"><h3 className="text-sm font-semibold text-content-primary">Top customers by billed</h3><span className="text-[11px] text-content-muted">Click a bar</span></div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={customerBarData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
            onClick={(e: { activePayload?: Array<{ payload: (typeof customerBarData)[number] }> }) => { const p = e?.activePayload?.[0]?.payload; if (p) setChartDrill({ title: `Invoices for customer ${p.fullKey}`, subtitle: `${p.detail.length} row(s) · ${inr(p.total)} billed`, rows: p.detail }); }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="name" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'var(--color-bg-card-hover)' }} formatter={(v) => [inr(Number(v)), 'Billed']} />
            <Bar dataKey="total" radius={[4, 4, 0, 0]} maxBarSize={40} className="cursor-pointer">{customerBarData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
  const tabs = [
    { id: 'kpis' as const, label: 'KPI Overview', content: kpisContent },
    { id: 'charts' as const, label: 'Charts', content: chartsContent },
  ];
  const activeContent = tabs.find((t) => t.id === tab)?.content;

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3">
        {searchOpen ? (
          <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-primary bg-surface-card px-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-content-muted" />
            {/* Length cap only — deliberately no injection guards.
                `query` never leaves the browser: it feeds a `useMemo`
                filter over already-loaded rows and is never rendered as
                HTML, so there is no query to inject into and nothing to
                escape. Capping guards against a pathological paste. */}
            <input ref={searchInputRef} value={query} onChange={(e) => setQuery(e.target.value.slice(0, 100))} maxLength={100} placeholder="Search # / status / customer..."
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setQuery(''); } }}
              className="min-w-0 flex-1 bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted" />
            <button type="button" aria-label="Close search" onClick={() => { setSearchOpen(false); setQuery(''); }}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-content-muted hover:bg-surface-card-hover"><XIcon className="h-3.5 w-3.5" /></button>
          </div>
        ) : (<h2 className="min-w-0 flex-1 truncate text-base font-semibold text-content-primary">Invoices dashboard</h2>)}
        <div className="flex shrink-0 items-center gap-2">
          {!searchOpen && (<button type="button" aria-label="Open search" onClick={() => setSearchOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-content-muted hover:bg-surface-card-hover"><Search className="h-4 w-4" /></button>)}
          {canExport && (<Button variant="outline" size="sm" onClick={printDashboard} disabled={printing}><Printer className={`h-3.5 w-3.5 ${printing ? 'animate-pulse' : ''}`} />{printing ? 'Printing…' : 'Print'}</Button>)}
          <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
        </div>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      <div ref={dashboardRef} className="space-y-3 bg-surface-page p-[3px]">
        <Tabs tabs={tabs} activeId={tab} onChange={(id) => setTab(id as TabId)} barOnly />
        <div>{activeContent}</div>
      </div>
      <DrilldownModal isOpen={!!openKpi} onClose={() => setOpenKpi(null)} title={openKpi ? `${openKpi.title} — details` : ''} subtitle={openKpi ? `${kpiDrillRows.length} row(s)` : ''} size="xl"
        actions={canExport ? <ExportButtonGroup onExcel={kpiExcel} onPdf={kpiPdf} onPrint={printWidget} compact /> : null}>
        <div ref={drillBodyRef}><DrillTable rows={kpiDrillRows} columns={DRILL_COLUMNS} /></div>
      </DrilldownModal>
      <DrilldownModal isOpen={!!chartDrill} onClose={() => setChartDrill(null)} title={chartDrill?.title ?? ''} subtitle={chartDrill?.subtitle} size="xl"
        actions={canExport ? <ExportButtonGroup onExcel={chartExcel} onPdf={chartPdf} onPrint={printWidget} compact /> : null}>
        <div ref={chartDrillBodyRef}><DrillTable rows={chartDrill?.rows ?? []} columns={DRILL_COLUMNS} /></div>
      </DrilldownModal>
    </div>
  );
}
